import { formatDistanceToNow } from 'date-fns';

import { PrerequisiteStatus, SATISFIED_STATUSES } from './types';

/* ------------------------------------------------------------------ */
/* Prerequisite status                                                 */
/* ------------------------------------------------------------------ */

/** Check if a prerequisite status counts as satisfied. Single source of truth. */
export function isSatisfied(status: PrerequisiteStatus | string): boolean {
  return SATISFIED_STATUSES.has(status as PrerequisiteStatus);
}

/* ------------------------------------------------------------------ */
/* Currency                                                            */
/* ------------------------------------------------------------------ */

/** Format a decimal string as USD currency. Returns '-' for null/empty. */
export function formatCurrency(value: string | null): string {
  if (!value) return '-';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(num);
}

/* ------------------------------------------------------------------ */
/* Date / time formatting                                              */
/* ------------------------------------------------------------------ */

/** "Mar 15" */
export function formatShortDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/** "March 15, 2025" */
export function formatFullDate(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Mar 15, 2025, 2:30 PM" */
export function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** "3 days ago" — wraps date-fns formatDistanceToNow */
export function formatRelativeDate(dateString: string): string {
  return formatDistanceToNow(new Date(dateString), { addSuffix: true });
}

/* ------------------------------------------------------------------ */
/* Time-in-stage                                                       */
/* ------------------------------------------------------------------ */

/** Compute hours elapsed since a given ISO timestamp */
export function hoursInStage(stageEnteredAt: string): number {
  const entered = new Date(stageEnteredAt);
  const now = new Date();
  return (now.getTime() - entered.getTime()) / (1000 * 60 * 60);
}

/** Format duration as human-readable: "< 1h", "4h", "2d 6h", "14d" */
export function formatDuration(hours: number): string {
  if (hours < 1) return '< 1h';
  if (hours < 24) return `${Math.floor(hours)}h`;
  const days = Math.floor(hours / 24);
  const remainingHours = Math.floor(hours % 24);
  if (remainingHours === 0) return `${days}d`;
  return `${days}d ${remainingHours}h`;
}

/** "Jan 15, 2026 (74 days ago)" */
export function formatDateWithRelative(dateString: string | null): string {
  if (!dateString) return '-';
  const absolute = new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const relative = formatDistanceToNow(new Date(dateString), { addSuffix: true });
  return `${absolute} (${relative})`;
}

/* ------------------------------------------------------------------ */
/* Prerequisite human labels                                           */
/* ------------------------------------------------------------------ */

const PREREQ_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  extracted: 'Found',
  processed: 'Verified',
  complete: 'Complete',
  approved: 'Approved',
  failed: 'Failed',
  manual: 'Manual Entry',
  waived: 'Waived',
};

/** Map backend prerequisite status values to human-friendly labels */
export function humanizePrereqStatus(status: string): string {
  return PREREQ_STATUS_LABELS[status] ?? status;
}

/* ------------------------------------------------------------------ */
/* API error extraction                                                */
/* ------------------------------------------------------------------ */

/** Extract a human-readable error message from an RTK Query error */
export function extractApiError(err: unknown, fallback: string): string {
  const data = (err as { data?: { detail?: string; message?: string } })?.data;
  return data?.detail ?? data?.message ?? fallback;
}
